/**
 * Key people (KYB): declare the company's UBOs, directors and representatives
 * (Didit "Key people"), then wait while each completes their own KYC
 * ("Awaiting users"). Both own their calls and hand back the next snapshot.
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CollectionFlowError, getCollectionFlow, postCollectionFlowEvent } from './client';
import { CopyButton } from './CopyButton';
import { CountrySelect } from './CountrySelect';
import {
  type CompanyProgress,
  declarationOf,
  emptyPerson,
  getCompanyProgress,
  KEY_PEOPLE_EVENT,
  type KeyPeopleView,
  type KeyPerson,
  listKeyPeople,
  type PersonDraft,
  personProblem,
  type Role,
} from './keyPeople';
import type { CollectionFlowSnapshot } from './types';
import { Illustration } from '../ui/Illustration';

interface Props {
  token: string;
  onAdvance: (snapshot: CollectionFlowSnapshot) => void;
}

export function KeyPeopleStep({ token, view, onAdvance }: Props & { view: KeyPeopleView }) {
  const { t } = useTranslation();
  const [people, setPeople] = useState<PersonDraft[]>([emptyPerson()]);
  const [unidentifiable, setUnidentifiable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const problems = people.map((p) => personProblem(p, view));
  const hasUbo = people.some((p) => p.roles.includes('ubo'));
  // Every company has directors, and each of them verifies themselves: the
  // server refuses a declaration without one (`key_people::validate`).
  const hasDirector = people.some((p) => p.roles.includes('director'));
  const ready = problems.every((p) => p === null) && (hasUbo || unidentifiable) && hasDirector;

  const set = (i: number, patch: Partial<PersonDraft>) =>
    setPeople(people.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const toggleRole = (i: number, role: Role) => {
    const roles = people[i]?.roles ?? [];
    set(i, {
      roles: roles.includes(role) ? roles.filter((r) => r !== role) : [...roles, role],
    });
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      onAdvance(
        await postCollectionFlowEvent(
          token,
          KEY_PEOPLE_EVENT,
          declarationOf(people, unidentifiable),
        ),
      );
    } catch (e) {
      setError(
        e instanceof CollectionFlowError && e.code === 'invalid_key_people'
          ? t('keyPeople.error.invalid')
          : t('company.error.generic'),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="details-form">
      <h1 className="step-title">{t('keyPeople.title')}</h1>
      <p className="step-text">{t('keyPeople.lead', { threshold: view.ubo_threshold })}</p>
      {people.map((person, i) => (
        <fieldset className="key-person" key={i}>
          <legend className="details-label">{t('keyPeople.person', { n: i + 1 })}</legend>
          <label className="details-field">
            <span className="details-label">{t('keyPeople.name')}</span>
            <input
              className="details-input"
              onChange={(e) => set(i, { name: e.target.value })}
              value={person.name}
            />
          </label>
          <label className="details-field">
            <span className="details-label">{t('keyPeople.email')}</span>
            <input
              className="details-input"
              inputMode="email"
              onChange={(e) => set(i, { email: e.target.value })}
              type="email"
              value={person.email}
            />
          </label>
          <div className="details-field">
            <span className="details-label">{t('keyPeople.nationality')}</span>
            <CountrySelect
              label={t('keyPeople.nationality')}
              onChange={(code) => set(i, { nationality: code })}
              placeholder={t('keyPeople.chooseNationality')}
              value={person.nationality}
            />
          </div>
          <div className="details-field" role="group" aria-label={t('keyPeople.roles')}>
            <span className="details-label">{t('keyPeople.roles')}</span>
            <div className="company-result__pills">
              {view.roles.map((role) => (
                <label className="pill" key={role}>
                  <input
                    checked={person.roles.includes(role)}
                    onChange={() => toggleRole(i, role)}
                    type="checkbox"
                  />{' '}
                  {t(`keyPeople.role.${role}`)}
                </label>
              ))}
            </div>
          </div>
          {person.roles.includes('ubo') || person.roles.includes('shareholder') ? (
            <label className="details-field">
              <span className="details-label">{t('keyPeople.ownership')}</span>
              <input
                className="details-input"
                inputMode="decimal"
                onChange={(e) => set(i, { ownership: e.target.value })}
                value={person.ownership}
              />
            </label>
          ) : null}
          {people.length > 1 ? (
            <button
              className="link-button"
              onClick={() => setPeople(people.filter((_, j) => j !== i))}
              type="button"
            >
              {t('keyPeople.remove')}
            </button>
          ) : null}
        </fieldset>
      ))}
      <button
        className="btn btn--ghost"
        onClick={() => setPeople([...people, emptyPerson()])}
        type="button"
      >
        {t('keyPeople.add')}
      </button>
      {view.allow_unidentifiable_ubos ? (
        <label className="key-person__check">
          <input
            checked={unidentifiable}
            onChange={(e) => setUnidentifiable(e.target.checked)}
            type="checkbox"
          />{' '}
          {t('keyPeople.unidentifiable')}
        </label>
      ) : null}
      {hasDirector ? null : <p className="step-text">{t('keyPeople.needsDirector')}</p>}
      {error ? (
        <p className="alert" role="alert">
          {error}
        </p>
      ) : null}
      <div className="actions">
        <button
          className="btn"
          disabled={!ready || busy}
          onClick={() => void submit()}
          type="button"
        >
          {t('company.confirm.submit')}
        </button>
      </div>
    </div>
  );
}

/** How often the awaiting screen re-reads the people while any are pending. */
const POLL_MS = 10_000;

/**
 * Re-reads the people while any are pending. Once none is, it re-reads the case
 * and hands it back: the platform moves the case on when the last person's KYC
 * is decided, and the realtime signal for that may never arrive.
 */
export function AwaitingUsers({ token, onAdvance }: Props) {
  const { t } = useTranslation();
  const [people, setPeople] = useState<KeyPerson[] | null>(null);

  useEffect(() => {
    let alive = true;
    const load = () =>
      listKeyPeople(token)
        .then(async (next) => {
          if (!alive) return;
          setPeople(next);
          if (next.length > 0 && next.every((p) => p.kyc_status !== 'pending')) {
            const snapshot = await getCollectionFlow(token);
            if (alive) onAdvance(snapshot);
          }
        })
        .catch(() => undefined);
    void load();
    const timer = window.setInterval(() => void load(), POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [token, onAdvance]);

  return (
    <div className="details-form">
      <h1 className="step-title">{t('keyPeople.awaiting.title')}</h1>
      <p className="step-text">{t('keyPeople.awaiting.lead')}</p>
      <ul className="company-results">
        {(people ?? []).map((p, i) => (
          <li className="company-result" key={i}>
            <span className="company-result__body">
              <span className="company-result__name">{p.name ?? '—'}</span>
              <span className="step-text">
                {p.roles.map((r) => t(`keyPeople.role.${r}`)).join(' · ')}
                {p.ownership_percent === null ? '' : ` · ${p.ownership_percent}%`}
              </span>
              <span className="company-result__pills">
                <span className="pill">{t(`keyPeople.status.${p.kyc_status}`)}</span>
              </span>
              {p.link ? (
                <CopyButton
                  className="btn btn--ghost"
                  copiedLabel={t('keyPeople.awaiting.copied')}
                  idleLabel={t('keyPeople.awaiting.copy')}
                  value={p.link}
                />
              ) : null}
            </span>
          </li>
        ))}
      </ul>
      <p className="step-text">{t('keyPeople.awaiting.auto')}</p>
    </div>
  );
}

/**
 * The key person's own closing screen ("awaiting company"): their part is in,
 * and this is where the company's verification stands. Polls until it is
 * finished. Shown in place of the plain submitted / approved screen.
 */
export function AwaitingCompany({ token, initial }: { token: string; initial: CompanyProgress }) {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(initial);
  const finished = progress.stage === 'finished';

  useEffect(() => {
    if (finished) return;
    let alive = true;
    const timer = window.setInterval(() => {
      void getCompanyProgress(token)
        .then((next) => {
          if (alive && next) setProgress(next);
        })
        .catch(() => undefined);
    }, POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [token, finished]);

  const company = progress.company_name ?? t('keyPeople.company.fallbackName');
  return (
    <main className="screen">
      <div className="card card--hero enter" data-testid="awaiting-company">
        <Illustration kind="submitted" />
        <h1 className="hero-title">{t('keyPeople.company.title')}</h1>
        <p className="hero-text">{t(`keyPeople.company.body.${progress.stage}`, { company })}</p>
        <span className="pill">{t(`keyPeople.company.stage.${progress.stage}`)}</span>
      </div>
    </main>
  );
}
